import { catchError, defer, finalize, from, type Observable, of, tap, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import useApi from './use-api';
import { type AuthSession } from '../types';
import Utils from '../utils/utils';

interface AuthType {
  getCurrentSession: () => AuthSession | undefined;
  login: (email: string) => Observable<AuthSession>;
  loginVerify: (email: string, otp: string) => Observable<AuthSession>;
  completeProfile: (
    email: string,
    currentPassword: string,
    newPassword: string,
    attributes: Record<string, string>
  ) => Observable<AuthSession>;
  loginPath: string;
  logout: (accessToken: string) => Observable<any>;
  refreshToken: (accessToken: string, refreshToken: string) => Observable<AuthSession>;
  switchOrganization: (
    organizationId: string,
    accessToken: string,
    refreshToken: string
  ) => Observable<AuthSession>;
  resetPassword: (email: string) => Observable<any>;
  changePassword: (
    email: string,
    currentPassword: string,
    newPassword: string,
    accessToken: string
  ) => Observable<any>;
  changePasswordByToken: (email: string, token: string, newPassword: string) => Observable<any>;
}

const headers = {
  accept: 'application/json',
  'content-type': 'application/json',
};

const useAuth = (): AuthType => {
  const api = useApi();

  const getCurrentSession = (): AuthSession | undefined =>
    JSON.parse(localStorage.getItem('session') ?? 'null');

  const login = (email: string): Observable<any> =>
    defer(
      async () =>
        await fetch(
          process.env.REACT_APP_API_BASE_URL! + process.env.REACT_APP_AUTH_ENDPOINT + '/login',
          {
            method: 'POST',
            body: ((username: string) => {
              const formData = new FormData();
              formData.append('username', username);
              return formData;
            })(email),
          }
        )
    ).pipe(
      switchMap((response) =>
        response.ok
          ? of({})
          : from(response.json()).pipe(switchMap((json) => throwError(() => new Error(json.error))))
      )
    );

  const loginVerify = (email: string, token: string): Observable<AuthSession> =>
    defer(
      async () =>
        await fetch(
          process.env.REACT_APP_API_BASE_URL! +
            process.env.REACT_APP_AUTH_ENDPOINT +
            '/verify?email=' +
            encodeURIComponent(email) +
            '&token=' +
            token,
          {
            method: 'POST',
          }
        )
    ).pipe(
      switchMap((response) =>
        response.ok
          ? from(response.json())
          : from(response.json()).pipe(switchMap((json) => throwError(() => new Error(json.error))))
      ),
      tap((session) => {
        localStorage.setItem('session', JSON.stringify(session));
      })
    );

  const completeProfile = (
    email: string,
    currentPassword: string,
    newPassword: string,
    attributes: Record<string, string>
  ): Observable<AuthSession> =>
    api.post(
      process.env.REACT_APP_AUTH_COMPLETE_PROFILE_ENDPOINT!,
      { email, currentPassword, newPassword, requiredAttributes: attributes },
      {},
      headers
    );

  const changePasswordByToken = (
    email: string,
    token: string,
    newPassword: string
  ): Observable<any> =>
    api.post(
      process.env.REACT_APP_AUTH_CHANGE_PASSWORD_BY_TOKEN_ENDPOINT!,
      { email, token, newPassword },
      {},
      headers
    );

  const changePassword = (
    email: string,
    currentPassword: string,
    newPassword: string,
    accessToken: string
  ): Observable<any> =>
    api.post(
      process.env.REACT_APP_AUTH_CHANGE_PASSWORD_ENDPOINT!,
      { email, currentPassword, newPassword },
      {},
      { ...headers, ...Utils.getAuthorizationHeader(accessToken) }
    );

  const resetPassword = (email: string): Observable<any> =>
    api.post(process.env.REACT_APP_AUTH_RESET_PASSWORD_ENDPOINT!, { email }, {}, headers);

  const logout = (accessToken: string): Observable<any> =>
    api
      .delete(
        process.env.REACT_APP_AUTH_ENDPOINT! + '/logout',
        {},
        {},
        { ...headers, ...Utils.getAuthorizationHeader(accessToken) }
      )
      .pipe(
        finalize(() => {
          localStorage.removeItem('session');
        })
      );

  const refreshToken = (_accessToken: string, refreshToken: string): Observable<AuthSession> =>
    api.post(process.env.REACT_APP_AUTH_ENDPOINT! + '/refresh', { refreshToken }, {}, headers).pipe(
      tap((session) => {
        localStorage.setItem('session', JSON.stringify(session));
      }),
      catchError((error) => {
        localStorage.removeItem('session');
        return throwError(() => error);
      })
    );

  const switchOrganization = (
    organizationId: string,
    accessToken: string,
    refreshToken: string
  ): Observable<AuthSession> =>
    api
      .post(
        process.env.REACT_APP_AUTH_SWITCH_ORGANIZATION_ENDPOINT! + `/${organizationId}`,
        { refreshToken },
        {},
        { ...headers, ...Utils.getAuthorizationHeader(accessToken) }
      )
      .pipe(
        tap((session) => {
          localStorage.setItem('session', JSON.stringify(session));
        })
      );

  return {
    loginPath: '/login',
    getCurrentSession,
    login,
    loginVerify,
    completeProfile,
    logout,
    refreshToken,
    switchOrganization,
    resetPassword,
    changePassword,
    changePasswordByToken,
  };
};

export default useAuth;
